import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, PauseCircle, PlayCircle, X, Calendar, Sun, Moon, Plus, Minus, Check, Sparkles, Edit3, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR, formatDate, todayISO } from "../../lib/utils.fmt";
import { addDaysISO, todayIST, tomorrowIST } from "../../lib/istDate";
import { buildPauseRequest, minResumeDate, pauseSummary, pauseWindowFor } from "../../lib/subscriptionPause";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import SubscriptionProductChange from "../../components/SubscriptionProductChange";
import { dialogAfterChange, hasUnsavedEdits, isProductChangeOn } from "../../lib/subscriptionProduct";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Human-readable schedule line for a subscription card. */
function scheduleLabel(sub) {
  const freq = (sub?.frequency || "").toLowerCase();
  const days = sub?.custom_days || [];
  if (freq === "daily") return "Daily";
  if (freq === "alternate") return `Alternate days · starts ${sub.start_date || "—"}`;
  if (freq === "weekly") {
    return days.length
      ? `Weekly · ${days.slice().sort().map((d) => DAY_SHORT[d]).join(", ")}`
      : "Weekly";
  }
  if (freq === "custom") {
    return days.length
      ? `Custom · ${days.slice().sort().map((d) => DAY_SHORT[d]).join(", ")}`
      : "Custom";
  }
  return (freq || "—").replace(/^\w/, (c) => c.toUpperCase());
}

export default function Subscriptions() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [pauseTarget, setPauseTarget] = useState(null);    // sub being paused
  const [resumeDate, setResumeDate] = useState("");
  // Earliest day a pause may start (tenant cutoff aware, from the server).
  const [pauseEarliest, setPauseEarliest] = useState("");
  const [busy, setBusy] = useState(false);
  const [trial, setTrial] = useState({ enabled: false, eligible: false, days: 0 });
  // Edit-qty dialog
  const [editTarget, setEditTarget] = useState(null);
  const [editQty, setEditQty] = useState(1);
  const [editInstructions, setEditInstructions] = useState("");
  const [editSlot, setEditSlot] = useState("morning");
  const [editDays, setEditDays] = useState([]);
  const [editFreq, setEditFreq] = useState("daily");
  const [editStartDate, setEditStartDate] = useState("");
  const [tenantSlots, setTenantSlots] = useState([]);
  const [addresses, setAddresses] = useState([]);
  // "Change product / size" in the edit dialog — only in stores that have it
  // switched on (components/SubscriptionProductChange).
  const [productChange, setProductChange] = useState({ on: false, products: [] });
  // The edit form as it was when the dialog opened — a product change closes
  // the dialog, so it is not offered while the form has edits of its own.
  const [editOpened, setEditOpened] = useState(null);

  useEffect(() => {
    // Signed-out visitors get the "Sign in" card below. Asking the signed-in
    // endpoint for them answers 401, which sends the app to the login screen
    // before that card can show.
    if (!user) return;
    api.get("/api/customer/home")
       .then(({ data }) => {
         setTenantSlots((data?.tenant?.delivery_windows || []).filter((w) => w.is_active !== false));
         setProductChange({ on: isProductChangeOn(data?.tenant), products: data?.products || [] });
       })
       .catch(() => {});
  }, [user]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = () => {
    setLoadError(null);
    return api.get("/api/customer/subscriptions").then(({ data }) => setSubs(data.subscriptions))
      .catch((e) => setLoadError(friendlyMessage(e)));
  };
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    load().finally(() => setLoading(false));
    api.get("/api/customer/home").then(({ data }) => setTrial(data.trial || { enabled: false, eligible: false, days: 0 })).catch(() => {});
    api.get("/api/customer/addresses").then(({ data }) => setAddresses(data.addresses || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Slots the edit-target's area actually allows (empty = no restriction).
  const editAreaAllowedSlots = addresses.find((a) => a.id === editTarget?.address_id)?.allowed_slots || [];
  const editableTenantSlots = editAreaAllowedSlots.length
    ? tenantSlots.filter((w) => editAreaAllowedSlots.includes((w.label || "").toLowerCase()))
    : tenantSlots;

  // Cancel target + preview (locked deliveries that will still arrive).
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);

  if (!user) return <LoginRequired title="Sign in to view subscriptions" description="Manage your recurring deliveries — pause, skip or cancel anytime." />;
  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load your subscriptions" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  const openPause = async (sub) => {
    setPauseTarget(sub);
    // The pause starts on the earliest date the cutoff still allows; the
    // server is the authority for that (IST, per-slot cutoffs).
    let earliest = tomorrowIST();
    try {
      // This subscription's OWN slot: a day whose cutoff has passed is
      // delivered anyway, so the pause starts on the first day it can skip.
      const { data } = await api.get("/api/customer/cutoff-info", { params: { slot: sub.slot } });
      if (data?.earliest_action_date) earliest = data.earliest_action_date;
    } catch { /* fall back to tomorrow — the server re-validates anyway */ }
    setPauseEarliest(earliest);
    // Default: a 7-day pause.
    setResumeDate(addDaysISO(earliest, 7));
  };

  const confirmPause = async () => {
    if (!pauseTarget || !resumeDate) return;
    const req = buildPauseRequest(pauseEarliest || tomorrowIST(), resumeDate);
    if (req.error) { toast.error(req.error); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/api/customer/subscriptions/${pauseTarget.id}/pause`, req.body);
      toast.success(`Paused — deliveries resume ${formatDate(data?.resumes_on || resumeDate)}`);
      if (data?.locked?.length) {
        toast.message(`${data.locked.length} delivery already past its cutoff will still arrive.`);
      }
      setPauseTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Pause failed"); }
    finally { setBusy(false); }
  };

  const resume = async (id) => {
    try {
      const { data } = await api.patch(`/api/customer/subscriptions/${id}/resume`);
      // After the day's cutoff the resume starts from the next eligible day —
      // say so, instead of letting the customer expect today's delivery.
      toast.success(data?.resumes_from && data.resumes_from !== todayIST()
        ? `Resumed — deliveries start ${formatDate(data.resumes_from)}`
        : "Resumed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resume failed"); }
  };

  // Cancel target + preview (locked deliveries that will still arrive).
  const openCancel = async (sub) => {
    setCancelTarget(sub);
    setCancelPreview(null);
    try {
      const { data } = await api.get(`/api/customer/subscriptions/${sub.id}/cancel-preview`);
      setCancelPreview(data);
    } catch {
      // If preview fails, still allow cancel — just without the disclosure.
      setCancelPreview({ cancellable_count: 0, locked: [] });
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setBusy(true);
    try {
      await api.delete(`/api/customer/subscriptions/${cancelTarget.id}`);
      const lockedCount = (cancelPreview?.locked || []).length;
      toast.success(lockedCount > 0
        ? `Cancelled · ${lockedCount} already-locked delivery will still arrive`
        : "Subscription cancelled");
      setCancelTarget(null); setCancelPreview(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const openEdit = (sub) => {
    setEditTarget(sub);
    setEditQty(sub.quantity || 1);
    setEditInstructions(sub.instructions || "");
    setEditSlot((sub.slot || "morning").toLowerCase());
    setEditDays(sub.custom_days || []);
    setEditFreq((sub.frequency || "daily").toLowerCase());
    setEditStartDate(sub.start_date || todayISO());
    setEditOpened({
      quantity: sub.quantity || 1, instructions: sub.instructions || "", slot: (sub.slot || "morning").toLowerCase(),
      custom_days: sub.custom_days || [], frequency: (sub.frequency || "daily").toLowerCase(), start_date: sub.start_date || todayISO(),
    });
  };
  const saveEdit = async () => {
    if (editQty < 1 || editQty > 50) { toast.error("Qty must be 1–50"); return; }
    if ((editFreq === "weekly" || editFreq === "custom") && editDays.length === 0) {
      toast.error("Pick at least one delivery day"); return;
    }
    setBusy(true);
    try {
      const payload = {
        quantity: editQty,
        instructions: editInstructions,
        slot: editSlot,
        frequency: editFreq,
        start_date: editStartDate,
      };
      if (editFreq === "weekly" || editFreq === "custom") {
        payload.custom_days = editDays;
      } else {
        // Reset to empty list for non-day-of-week frequencies so the new
        // schedule isn't accidentally filtered by stale weekday data.
        payload.custom_days = [];
      }
      const { data } = await api.patch(`/api/customer/subscriptions/${editTarget.id}`, payload);
      const refreshed = data?.future_deliveries_updated || 0;
      toast.success(`Subscription updated${refreshed ? ` · ${refreshed} upcoming day${refreshed === 1 ? "" : "s"} refreshed` : ""}`);
      setEditTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const statusColor = (s) => ({
    active: "bg-brand-leaf/10 text-brand-leaf border-brand-leaf/30",
    paused: "bg-brand-sand/10 text-brand-sand border-brand-sand/30",
    vacation: "bg-brand-sky/10 text-brand-sky border-brand-sky/30",
    cancelled: "bg-neutral-100 text-neutral-500 border-neutral-200",
  })[s] || "bg-neutral-100 text-neutral-500";

  // Hide cancelled by default unless user has nothing else
  const visible = subs.filter((s) => s.status !== "cancelled");
  const cancelledCount = subs.length - visible.length;

  return (
    <div className="page-enter" data-testid="subscriptions-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/home" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex-1">My Subscriptions</h1>
          <Link to="/app/products" className="w-10 h-10 rounded-full bg-brand-sky text-white flex items-center justify-center" data-testid="add-sub-btn">
            <Plus className="w-5 h-5" />
          </Link>
        </div>
      </header>

      <main className="px-5 space-y-3">
        {/* Trial CTA for eligible customers (no active subs yet AND tenant trials enabled). */}
        {visible.length === 0 && trial.eligible && (
          <Link to="/app/products" className="block" data-testid="trial-cta-empty">
            <div className="rounded-3xl bg-gradient-to-br from-brand-leaf to-brand-sky text-white p-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5"/>
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-white/85">Welcome offer</div>
                  <div className="font-heading text-lg font-extrabold leading-tight">
                    {trial.is_free
                      ? `Try us free for ${trial.days} days`
                      : `Try us for ${trial.days} days — no commitment`}
                  </div>
                  <div className="text-xs text-white/85 mt-1">
                    {trial.is_free
                      ? `Subscribe to any product · no wallet charge for the first ${trial.days} days · auto-cancels at trial end.`
                      : `Pay per delivery for ${trial.days} days, then auto-cancels. Re-subscribe anytime to continue.`}
                  </div>
                  <button className="mt-3 bg-white text-brand-ink rounded-2xl h-10 px-4 font-bold text-sm" data-testid="start-trial-btn">
                    Start trial →
                  </button>
                </div>
              </div>
            </div>
          </Link>
        )}

        {visible.length === 0 && !trial.eligible && (
          <div className="bg-white rounded-3xl border border-neutral-200">
            <EmptyState
              icon={Calendar}
              title="No active subscriptions"
              description="Browse products to start daily delivery."
              action={<Link to="/app/products"><Button className="bg-brand-sky hover:bg-blue-700 rounded-2xl h-12 px-6 font-bold" data-testid="shop-now-btn">Shop now</Button></Link>}
              testId="subs-empty"
            />
          </div>
        )}
        {visible.map((s) => (
          <div key={s.id} className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid={`sub-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-lg leading-tight truncate">{s.product_name}</div>
                <div className="text-sm text-neutral-600 mt-1 capitalize flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    {s.slot === "morning" ? <Sun className="w-3.5 h-3.5"/> : s.slot === "evening" ? <Moon className="w-3.5 h-3.5"/> : <Calendar className="w-3.5 h-3.5"/>}
                    {s.slot}
                  </span>
                  · Qty {s.quantity}
                </div>
                <div className="text-sm font-bold text-brand-ink mt-1" data-testid={`sub-schedule-${s.id}`}>
                  {scheduleLabel(s)}
                </div>
                <div className="text-xs text-neutral-500 mt-1">Since {formatDate(s.start_date)}</div>
                {s.status === "paused" && (
                  <div className="text-xs text-brand-sand font-bold mt-1 inline-flex items-center gap-1" data-testid={`sub-pause-window-${s.id}`}>
                    <PauseCircle className="w-3 h-3"/> {pauseSummary(s, formatDate)}
                  </div>
                )}
                {/* Trial badge — surface while still inside the trial window
                    so the customer knows the sub will auto-cancel at the end
                    AND whether deliveries during the trial are free or paid. */}
                {s.is_trial && s.trial_expires_at && s.trial_expires_at >= todayIST() && (
                  <div className="text-xs text-brand-leaf font-extrabold mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-leaf/10" data-testid={`trial-badge-${s.id}`}>
                    {(s.trial_billing || "free") === "free" ? "🎉 FREE TRIAL" : "🕒 TRIAL"} · ends {formatDate(s.trial_expires_at)}
                  </div>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${statusColor(s.status)}`}>{s.status}</span>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
              <div className="font-mono font-bold">{formatINR(s.price_per_unit * s.quantity)}/delivery</div>
              <div className="flex gap-2 flex-wrap">
                {s.status !== "cancelled" && (
                  <button onClick={() => openEdit(s)}
                          className="px-3 h-10 rounded-2xl bg-brand-sky/10 text-brand-sky font-bold text-sm inline-flex items-center gap-1.5"
                          data-testid={`edit-${s.id}`}>
                    <Edit3 className="w-4 h-4" /> Edit
                  </button>
                )}
                {s.status === "active" ? (
                  <button onClick={() => openPause(s)} className="px-3 h-10 rounded-2xl bg-brand-sand/10 text-brand-sand font-bold text-sm inline-flex items-center gap-1.5" data-testid={`pause-${s.id}`}>
                    <PauseCircle className="w-4 h-4" /> Pause
                  </button>
                ) : (
                  <button onClick={() => resume(s.id)} className="px-3 h-10 rounded-2xl bg-brand-leaf/10 text-brand-leaf font-bold text-sm inline-flex items-center gap-1.5" data-testid={`resume-${s.id}`}>
                    <PlayCircle className="w-4 h-4" /> Resume
                  </button>
                )}
                <button onClick={() => openCancel(s)} className="px-3 h-10 rounded-2xl bg-neutral-100 text-neutral-600 font-bold text-sm inline-flex items-center gap-1.5" data-testid={`cancel-${s.id}`}>
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </div>
          </div>
        ))}
        {cancelledCount > 0 && (
          <div className="text-xs text-center text-neutral-400 pt-2">
            + {cancelledCount} cancelled subscription{cancelledCount > 1 ? "s" : ""} hidden
          </div>
        )}
      </main>

      <Dialog open={!!pauseTarget} onOpenChange={(o) => !o && setPauseTarget(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="pause-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Pause subscription</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-600 -mt-1">
            Going out of town? Pick when you want deliveries to resume.
          </p>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Resume on</label>
            <Input type="date" value={resumeDate} min={minResumeDate(pauseEarliest || tomorrowIST())}
                   onChange={(e) => setResumeDate(e.target.value)}
                   className="h-12 rounded-2xl mt-1" data-testid="pause-resume-date"/>
          </div>
          {/* Quick presets */}
          <div className="flex gap-2 flex-wrap">
            {[1, 3, 7, 14, 30].map((n) => (
              <button key={n}
                      onClick={() => setResumeDate(addDaysISO(pauseEarliest || tomorrowIST(), n))}
                      className="h-9 px-3 rounded-full text-xs font-bold border border-neutral-200 bg-white text-neutral-700"
                      data-testid={`pause-quick-${n}`}>{n} day{n === 1 ? "" : "s"}</button>
            ))}
          </div>
          {(() => {
            const w = pauseWindowFor(pauseEarliest || tomorrowIST(), resumeDate);
            return w ? (
              <div className="rounded-2xl bg-brand-cream px-3 py-2 text-xs text-neutral-700" data-testid="pause-summary">
                No deliveries <b>{formatDate(w.from)}</b>{w.from !== w.until ? <> – <b>{formatDate(w.until)}</b></> : null}.
                {" "}Deliveries resume <b>{formatDate(resumeDate)}</b>.
              </div>
            ) : null;
          })()}
          <Button onClick={confirmPause} disabled={busy || !resumeDate}
                  className="w-full h-12 rounded-2xl bg-brand-sand hover:bg-orange-600 font-bold text-white" data-testid="confirm-pause-btn">
            <Check className="w-4 h-4 mr-2"/> Pause · resumes {resumeDate ? formatDate(resumeDate) : "—"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelPreview(null); } }}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Cancel subscription?</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              {cancelTarget ? `Your ${cancelTarget.product_name} subscription will stop. Re-subscribe anytime.` : ""}
            </DialogDescription>
          </DialogHeader>
          {/* Only show the locked-delivery disclosure when cutoff has already
              passed for at least one upcoming row — keeps the dialog quiet
              for the common case. */}
          {cancelPreview && cancelPreview.locked && cancelPreview.locked.length > 0 && (
            <div className="rounded-2xl bg-brand-sand/15 border border-brand-sand/40 p-3 text-sm" data-testid="cancel-locked-warning">
              <div className="font-bold text-brand-ink mb-1">
                {cancelPreview.locked.length} delivery already locked
              </div>
              <div className="text-neutral-700 text-xs">
                The following {cancelPreview.locked.length === 1 ? "delivery is" : "deliveries are"} past the order cutoff and will still arrive (wallet debited normally):
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {cancelPreview.locked.map((l) => (
                  <li key={l.id} className="font-mono text-neutral-700" data-testid={`locked-row-${l.id}`}>
                    • {formatDate(l.date)} · {l.slot}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {cancelPreview && (
            <div className="text-xs text-neutral-500" data-testid="cancel-preview-summary">
              {cancelPreview.cancellable_count > 0
                ? `${cancelPreview.cancellable_count} upcoming ${cancelPreview.cancellable_count === 1 ? "delivery" : "deliveries"} will be cancelled.`
                : "No upcoming deliveries to cancel."}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setCancelTarget(null); setCancelPreview(null); }}
                    className="flex-1 h-12 rounded-2xl bg-neutral-100 text-neutral-700 font-bold text-sm"
                    data-testid="cancel-dismiss-btn">
              Keep subscription
            </button>
            <Button onClick={confirmCancel} disabled={busy || !cancelPreview}
                    className="flex-1 h-12 rounded-2xl bg-brand-clay hover:bg-red-700 text-white font-bold"
                    data-testid="cancel-confirm-btn">
              {busy ? "Cancelling…" : "Cancel subscription"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="edit-sub-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit subscription</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">Changes apply to all future deliveries. Locked-by-cutoff days stay as-is.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <>
              <div className="bg-brand-cream rounded-2xl p-3">
                <div className="font-bold">{editTarget.product_name}</div>
                <div className="text-xs text-neutral-600 capitalize">{scheduleLabel(editTarget)}</div>
              </div>
              {productChange.on && (
                <SubscriptionProductChange
                  key={editTarget.id}
                  sub={editTarget} products={productChange.products}
                  endpoint={`/api/customer/subscriptions/${editTarget.id}/product-change`}
                  unsaved={hasUnsavedEdits(editOpened, {
                    quantity: editQty, instructions: editInstructions, slot: editSlot,
                    custom_days: editDays, frequency: editFreq, start_date: editStartDate,
                  })}
                  onChanged={(data, outcome, { late } = {}) => {
                    load();
                    // An answer that arrives after its dialog was closed touches no dialog —
                    // by then this one may be showing another subscription.
                    if (late) return;
                    // Done: close, so nobody is left to "save" a form they never changed.
                    // Still being applied: stay open on the status the step shows.
                    setEditTarget((t) => dialogAfterChange(t, editTarget.id, data, outcome));
                  }}
                  onClose={() => setEditTarget((t) => (t && t.id === editTarget.id ? null : t))}
                />
              )}
              {editableTenantSlots.length > 1 && (
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Slot</label>
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="edit-slot-grid">
                    {editableTenantSlots.map((w) => {
                      const k = (w.label || "").toLowerCase();
                      const on = editSlot === k;
                      const Icon = k === "evening" ? Moon : Sun;
                      return (
                        <button key={k} onClick={() => setEditSlot(k)}
                                className={`h-12 rounded-2xl text-xs font-bold border-2 inline-flex flex-col items-center justify-center ${on ? "bg-brand-sky text-white border-transparent" : "bg-white border-neutral-200 text-neutral-700"}`}
                                data-testid={`edit-slot-${k}`}>
                          <span className="inline-flex items-center gap-1"><Icon className="w-3.5 h-3.5"/> {w.label}</span>
                          <span className={`text-[9px] font-mono ${on ? "text-white/85" : "text-neutral-500"}`}>{w.slot_start} – {w.slot_end}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Frequency</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="edit-freq-grid">
                  {[
                    { v: "daily",     l: "Daily" },
                    { v: "alternate", l: "Alternate" },
                    { v: "weekly",    l: "Weekly" },
                    { v: "custom",    l: "Custom" },
                  ].map((o) => {
                    const on = editFreq === o.v;
                    return (
                      <button key={o.v} type="button"
                              onClick={() => setEditFreq(o.v)}
                              className={`h-11 rounded-2xl text-xs font-bold border-2 ${on ? "bg-brand-sky text-white border-transparent" : "bg-white border-neutral-200 text-neutral-700"}`}
                              data-testid={`edit-freq-${o.v}`}>{o.l}</button>
                    );
                  })}
                </div>
              </div>
              {(editFreq === "weekly" || editFreq === "custom") && (
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Days</label>
                  <div className="flex gap-1.5 flex-wrap mt-2" data-testid="edit-days-row">
                    {DAY_SHORT.map((d, i) => {
                      const on = editDays.includes(i);
                      return (
                        <button key={i}
                                onClick={() => setEditDays((p) => on ? p.filter((x) => x !== i) : [...p, i])}
                                className={`h-9 px-3 rounded-full text-xs font-bold ${on ? "bg-brand-sky text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                                data-testid={`edit-day-${i}`}
                                aria-label={DAY_LONG[i]}>{d}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Start date</label>
                <Input type="date" value={editStartDate} min={todayISO()}
                       onChange={(e) => setEditStartDate(e.target.value)}
                       className="h-12 rounded-2xl mt-2" data-testid="edit-start-date"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Qty per delivery</label>
                <div className="flex items-center gap-3 mt-2">
                  <button onClick={() => setEditQty(Math.max(1, editQty - 1))}
                          className="w-12 h-12 rounded-2xl bg-brand-cream border border-neutral-200 inline-flex items-center justify-center"
                          data-testid="edit-qty-minus">
                    <Minus className="w-5 h-5"/>
                  </button>
                  <div className="font-heading font-extrabold text-3xl w-12 text-center tabular-nums" data-testid="edit-qty-value">{editQty}</div>
                  <button onClick={() => setEditQty(Math.min(50, editQty + 1))}
                          className="w-12 h-12 rounded-2xl bg-brand-sky text-white inline-flex items-center justify-center"
                          data-testid="edit-qty-plus">
                    <Plus className="w-5 h-5"/>
                  </button>
                  <div className="flex-1 text-right text-sm text-neutral-600">
                    {formatINR((editTarget.price_per_unit || 0) * editQty)}<span className="text-neutral-400 text-xs">/delivery</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Instructions</label>
                <Input value={editInstructions} onChange={(e) => setEditInstructions(e.target.value)}
                       placeholder="Leave at door, ring bell, etc."
                       className="h-12 rounded-2xl mt-2" data-testid="edit-instructions"/>
              </div>
              <div className="rounded-2xl bg-brand-leaf/10 border border-brand-leaf/30 px-3 py-2 text-[11px] text-brand-leaf font-bold">
                Changes apply to all future deliveries. Past + locked-by-cutoff days stay as-is.
              </div>
              <Button onClick={saveEdit} disabled={busy}
                      className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold text-white"
                      data-testid="save-edit-sub-btn">
                <Check className="w-4 h-4 mr-2"/> Save changes
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
